import { useMemo } from 'react';

import {
  type CustomRoleTaxonomySelection,
  type RoleTaxonomyVersionsQuery,
  TaxonomyType,
} from '@revelio/data-access';

import { getRoleTaxonomyId } from '../utils/roleTaxonomy';
import { useTaxonomyList } from './useTaxonomyList';
import { useTaxonomyLatestVersion } from './useTaxonomyVersions';

type RoleTaxonomy = RoleTaxonomyVersionsQuery['roleTaxonomies'][number];

const getRoleTaxonomyName = (
  taxonomySelection: CustomRoleTaxonomySelection
) => {
  if (taxonomySelection.ricsK10Id) {
    return `rics_k10_${taxonomySelection.ricsK10Id}`;
  }

  if (taxonomySelection.ricsK50Id) {
    return `rics_k50_${taxonomySelection.ricsK50Id}`;
  }

  return undefined;
};

const getDefaultVersion = (taxonomies: RoleTaxonomy[] | undefined) => {
  const latestDefaultTaxonomy = taxonomies?.find(
    (taxonomy) =>
      taxonomy.isLatest &&
      (taxonomy.taxonomyName === 'default' || taxonomy.taxonomyId === 0)
  );
  if (latestDefaultTaxonomy) return latestDefaultTaxonomy.version;

  const latestTaxonomy = taxonomies?.find((taxonomy) => taxonomy.isLatest);
  if (latestTaxonomy) return latestTaxonomy.version;

  return undefined;
};

const getSpecificVersion = ({
  taxonomies,
  taxonomySelection,
  defaultVersion,
}: {
  taxonomies: RoleTaxonomy[] | undefined;
  taxonomySelection: CustomRoleTaxonomySelection;
  defaultVersion: string | undefined;
}) => {
  if (taxonomySelection.default) return defaultVersion;

  const taxonomyId = getRoleTaxonomyId(taxonomySelection);
  const taxonomyName = getRoleTaxonomyName(taxonomySelection);
  const taxonomy = taxonomies?.find((taxonomy) => {
    if (taxonomyId !== undefined && taxonomy.taxonomyId === taxonomyId) {
      return true;
    }

    return taxonomy.taxonomyName === taxonomyName;
  });

  return taxonomy?.version;
};

export const useRoleTaxonomyVersions = () => {
  const {
    taxonomies: referenceTaxonomies,
    hasData: hasReferenceTaxonomies,
    fetching: referenceFetching,
    error: referenceError,
  } = useTaxonomyList({
    taxonomyType: TaxonomyType.Role,
  });
  const {
    version: latestReferenceVersion,
    hasData: hasLatestReferenceVersion,
    fetching: latestReferenceFetching,
    error: latestReferenceError,
  } = useTaxonomyLatestVersion({
    taxonomyType: TaxonomyType.Role,
  });

  const mappedReferenceTaxonomies = useMemo(() => {
    if (!hasReferenceTaxonomies || !hasLatestReferenceVersion) {
      return undefined;
    }

    return referenceTaxonomies.map(
      (taxonomy): RoleTaxonomy => ({
        taxonomyId: taxonomy.id,
        taxonomyName: taxonomy.name,
        version: taxonomy.version,
        isLatest:
          latestReferenceVersion !== undefined &&
          latestReferenceVersion !== null &&
          taxonomy.version === latestReferenceVersion,
      })
    );
  }, [
    hasLatestReferenceVersion,
    hasReferenceTaxonomies,
    latestReferenceVersion,
    referenceTaxonomies,
  ]);

  return {
    taxonomies: mappedReferenceTaxonomies,
    loading:
      referenceFetching ||
      latestReferenceFetching ||
      (!hasReferenceTaxonomies && !referenceError) ||
      (!hasLatestReferenceVersion && !latestReferenceError),
    error: referenceError ?? latestReferenceError,
  };
};

export const useCurrentRoleTaxonomyVersion = () => {
  const { taxonomies, loading, error } = useRoleTaxonomyVersions();
  const defaultVersion = getDefaultVersion(taxonomies);

  return {
    version: defaultVersion,
    taxonomies,
    loading: loading || (!taxonomies && !error),
    error,
    getSpecificTaxonomyVersion: (
      taxonomySelection: CustomRoleTaxonomySelection
    ) =>
      getSpecificVersion({
        taxonomies,
        taxonomySelection,
        defaultVersion,
      }),
  };
};
